import type { WebLocale } from '@/enums/web-locale';

import Vue, { computed, reactive, watch } from 'vue';
import { RawLocation, Route } from 'vue-router';
import { defineStore } from 'pinia';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';

import { getCollectionTypeByNewPageType } from '@/helpers/graphql-model-helper';
import { arrayOfStringsAreEquals } from '@/helpers/object-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { updateState } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import Provider from '@/interfaces/provider';
import { ProviderType } from '@/components/filter-bar/filter-bar.types';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import {
	CountryFilters,
	FilterCollection,
	FilterName,
	FilterState,
	getDefaultFilterCollection,
	platformCollectionTypes,
} from '@/stores/useFilterStoreTypes';
import { defaultSortingItemSelectedKeys } from '@/helpers/list-sorting-helper';
import { SnowplowFreemiumContext, type SnowplowContext } from '@/helpers/tracking/providers';

const fillFilterCollection = (filterCollection?: FilterCollection): FilterCollection => ({
	...getDefaultFilterCollection(),
	...filterCollection,
});

const getDefaultCountryFilters = () =>
	Object.fromEntries(
		platformCollectionTypes.map(collectionType => [collectionType, getDefaultFilterCollection()])
	) as CountryFilters;

const fillCountryFilters = (countryFilters?: CountryFilters) => {
	if (!countryFilters) {
		return getDefaultCountryFilters();
	}

	return Object.fromEntries(
		platformCollectionTypes.map(collectionType => [
			collectionType,
			fillFilterCollection(countryFilters[collectionType]),
		])
	) as CountryFilters;
};

function isRandomSort(sortBy: string | null, collectionType: CollectionType): boolean {
	return (
		[
			CollectionType.POPULAR,
			CollectionType.TV_SHOW_TRACKING,
			CollectionType.MY_LISTS,
			CollectionType.PUBLIC_LISTS,
		].includes(collectionType) && sortBy === TitleOrder.RANDOM
	);
}

export const useFilterStore = defineStore('filter', () => {
	const languageStore = useLanguageStore();
	const userStore = useUserStore();
	const routingStore = useRoutingStore();

	const state = reactive<FilterState>({});

	watch(
		() => languageStore.webLocale,
		(webLocale, oldWebLocale) => {
			if (webLocale !== oldWebLocale && !state[webLocale]) {
				if (process.server) return;

				initFiltersPerLocale(webLocale);
			}
		},
		{ immediate: false }
	);

	const globalGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.GLOBAL]);
	const seenlistGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.SEENLIST]);
	const popularGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.POPULAR]);
	const subgenreGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.POPULAR]);
	const upcomingGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.UPCOMING]);
	const newGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.NEW]);
	const searchGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.SEARCH]);
	const offersGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.OFFERS]);
	const tvshowtrackingGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.TV_SHOW_TRACKING]);
	const mylistsGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.MY_LISTS]);
	const myRentalsFilters = computed(() => state[languageStore.webLocale]?.[CollectionType.MY_RENTALS]);
	const publiclistsGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.PUBLIC_LISTS]);
	const titlelistGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.TITLELIST]);
	const sportsGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.SPORTS]);
	const editorialGetter = computed(() => state[languageStore.webLocale]?.[CollectionType.EDITORIAL]);
	const publicListsFilters = computed(() => state[languageStore.webLocale]?.[CollectionType.PUBLIC_LISTS]);

	function filters(collectionType?: CollectionType) {
		if (!collectionType) {
			return getDefaultFilterCollection();
		}
		return state[languageStore.webLocale]?.[collectionType] ?? getDefaultFilterCollection();
	}

	function getWebLocale(webLocale: WebLocale) {
		return state[webLocale];
	}

	/**
	 * Returns the whole current filter state for the current collection type
	 */
	const currentFilters = computed(() => {
		const webLocale = languageStore.webLocale;

		if (!state[webLocale]) {
			console.error('[useFilterStore] computed/currentFilters state not initialized for webLocale: ', webLocale);
			return getDefaultFilterCollection();
		}

		const collectionType = routingStore.lastActiveCollectionType;

		// TODO Remove after fix https://sentry.justwatch.com/organizations/justwatch/issues/25003
		// This value should never be undefined, but it mysteriously is!
		if (!state[webLocale][collectionType]) {
			captureMessageForSentry(
				'Filter collection udefined',
				{
					where: '[useFilterStore.ts] currentFilters',
					webLocale,
					collectionType,
					filterState: JSON.stringify(state),
					collection: JSON.stringify(state[webLocale][collectionType]),
				},
				'error'
			);
		}

		return state[webLocale][collectionType];
	});

	/**
	 * returns a single top level value from the current collection type filter
	 *
	 * @TODO rename to 'currentFilterValue'
	 */
	function currentFilter(filterName: FilterName) {
		return currentFilters.value[filterName];
	}

	const selectedProvidersType = computed(() => {
		if (!currentFilters.value.providers_type) {
			const collectionType = routingStore.activeCollectionType as CollectionType;
			const isListsTab = [
				CollectionType.MY_LISTS,
				CollectionType.PUBLIC_LISTS,
				CollectionType.TV_SHOW_TRACKING,
			].includes(collectionType);

			if (isListsTab) {
				return 'all';
			}

			return userStore.isLoggedIn ? 'my_services' : 'all';
		}

		return currentFilters.value.providers_type;
	});

	function selectedProvidersByType(type?: ProviderType): Provider[] {
		const constantStore = useConstantStore();
		const providerType = type ?? selectedProvidersType.value;

		if (providerType) {
			if (providerType === 'my_services') return constantStore.providers(true);
			if (providerType === 'all') return constantStore.providers(false);
			return constantStore.providersByType[providerType];
		}

		return [];
	}

	// Note: Use this selector to get a list of provider short names needed for GraphQL query
	function selectedProviders(isSeoFilterBar: boolean): string[] {
		const cf = currentFilters.value;
		if (isSeoFilterBar) {
			return cf.providers?.length === 0 ? userStore.settings.providers : cf.providers;
		}
		const providersType = selectedProvidersType.value;
		if ((cf.providers || []).length === 0 && providersType) {
			return providersType === 'all' ? [] : selectedProvidersByType().map((p: Provider) => p.shortName);
		}
		return cf.providers;
	}

	function setFilters(webLocale: WebLocale, collectionType: CollectionType, filterState: FilterCollection) {
		state[webLocale][collectionType] = { ...state[webLocale][collectionType], ...filterState };
	}

	function setFilterValueFunction(
		webLocale: WebLocale,
		collectionType: CollectionType | CollectionType[],
		filterName: FilterName,
		filterValue: any
	) {
		const collectionTypes = Array.isArray(collectionType) ? collectionType : [collectionType];
		for (const type of collectionTypes) {
			state[webLocale][type] = { ...state[webLocale][type], [filterName]: filterValue };
		}
	}

	/**
	 * sets the filter and updates the state.
	 */
	function setFilterValue({
		filterName,
		filterValue,
		collectionType,
		updateRouteState = true,
	}: {
		filterName: FilterName;
		filterValue: any;
		collectionType?: CollectionType;
		updateRouteState?: boolean;
	}) {
		const typeOfCollection = collectionType ?? (routingStore.activeCollectionType as CollectionType);
		const webLocale = languageStore.webLocale;
		setFilterValueFunction(webLocale, typeOfCollection, filterName, filterValue);
		if (updateRouteState) return updateRouterState({});
	}

	function updateRouterState(payload: {
		collectionType?: CollectionType;
		routeQuery?: Route['query'];
	}): RawLocation | undefined {
		const constantStore = useConstantStore();

		const { activeRoute, activeCollectionType } = routingStore;

		const activeNewPageType = getCollectionTypeByNewPageType(activeRoute?.meta?.newPageType);
		const { webLocale, collectionType, routeQuery } = {
			webLocale: languageStore.webLocale,
			collectionType: activeCollectionType as CollectionType,
			routeQuery: activeRoute?.query,
			...payload,
		};

		// watchlist always uses it's own set of filters
		if (collectionType !== 'titlelist') {
			return updateState(
				undefined,
				activeNewPageType && activeNewPageType !== CollectionType.NEW ? activeNewPageType : collectionType, // we don't keep newPageType in any filters, so we need to differentiate here between NewPageType and CollectionType
				state[webLocale][collectionType],
				constantStore.providersByShortName,
				constantStore.genresByShortName,
				routeQuery,
				constantStore.subgenresList
			);
		}
	}

	/**
	 * in case a language has been changed, we might need to re-initialize the state that has been initialized from `languageStore.state.webLocale`.
	 */
	function initFiltersPerLocale(webLocale: WebLocale) {
		Vue.set(state, webLocale, fillCountryFilters(state[webLocale]));
	}

	function setRange({
		rangeName,
		trackingKey,
		upper,
		lower,
		selectedState,
		updateRouteState = true,
		isPremiumFilter = false,
	}: {
		rangeName: 'price' | 'release_year' | 'runtime';
		trackingKey: string;
		upper: number | null;
		lower: number | null;
		selectedState?: 1 | 0;
		updateRouteState?: boolean;
		isPremiumFilter?: boolean;
	}) {
		const collectionType = routingStore.activeCollectionType as CollectionType;

		const filterKeys = {
			price: { lower: 'min_price', upper: 'max_price' },
			release_year: { lower: 'release_year_from', upper: 'release_year_until' },
			runtime: { lower: 'min_runtime', upper: 'max_runtime' },
		};

		const filterState = {
			[filterKeys[rangeName].lower]: lower,
			[filterKeys[rangeName].upper]: upper,
		} as any;

		const contexts: SnowplowContext[] = [];
		if (isPremiumFilter) {
			const isHideSeenDislikedTitlesEnabled = userStore.isHideSeenDislikedTitlesEnabled ?? false;
			contexts.push(new SnowplowFreemiumContext(isHideSeenDislikedTitlesEnabled));
		}

		TrackingHelper.trackFilterToggleEvent(
			{ label: rangeName, property: trackingKey, value: selectedState },
			contexts
		);

		setFilters(languageStore.webLocale, collectionType, filterState);
		if (updateRouteState) return updateRouterState({});
	}

	function setSorting({ sortBy, sortAsc }: { sortBy: string | null; sortAsc: boolean | null }) {
		const collectionType = routingStore.activeCollectionType as CollectionType;
		const filterState = { sort_by: sortBy, sort_asc: sortAsc } as any;

		if (isRandomSort(sortBy, collectionType)) {
			const seed = state[languageStore.webLocale][collectionType].sorting_random_seed ?? 0;
			filterState.sorting_random_seed = seed + 1;
		}

		setFilters(languageStore.webLocale, collectionType, filterState);

		// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'sort_by',
			property: sortBy?.toLowerCase(),
		});

		return updateRouterState({});
	}

	/** Set filter options for lists specifically with collection type aware defaults. */
	function setListSorting({ sortBy, sortAsc = null }: { sortBy: string; sortAsc?: boolean | null }) {
		const collectionType = routingStore.activeCollectionType as CollectionType;
		const defaultSelectedKey = defaultSortingItemSelectedKeys[collectionType];

		return setSorting({
			sortBy: sortBy === defaultSelectedKey ? null : sortBy,
			sortAsc: sortBy === defaultSelectedKey ? null : sortAsc,
		});
	}

	function resetFilter({
		filterName,
		updateRouteState = true,
	}: {
		filterName: FilterName;
		resetAllCollections?: boolean;
		updateRouteState?: boolean;
	}) {
		setFilterValue({
			filterValue: getDefaultFilterCollection()[filterName],
			filterName,
			updateRouteState,
		});

		if (updateRouteState) return updateRouterState({});
	}

	function resetFilters({
		filterNames,
		updateRouteState = true,
	}: {
		filterNames: FilterName[];
		updateRouteState: boolean;
	}) {
		const activeCollectionType = routingStore.activeCollectionType as CollectionType;
		const webLocale = languageStore.webLocale;

		filterNames.forEach(filterName => {
			setFilterValueFunction(
				webLocale,
				activeCollectionType,
				filterName,
				getDefaultFilterCollection()[filterName]
			);
		});

		if (updateRouteState) return updateRouterState({});
	}

	function resetPremiumFilters() {
		const premiumFilterNames: FilterName[] = [
			'min_runtime',
			'max_runtime',
			'production_countries',
			'exclude_production_countries',
			'sort_by',
			'scoring_filter_types',
		];

		const collectionTypes = [CollectionType.NEW, CollectionType.POPULAR];

		for (const collectionType of collectionTypes) {
			for (const filterName of premiumFilterNames) {
				setFilterValue({
					filterName,
					filterValue: getDefaultFilterCollection()[filterName],
					collectionType,
					updateRouteState: false,
				});
			}
		}

		return updateRouterState({});
	}

	async function reset(payload?: {
		collectionTypeOverride?: CollectionType;
		updateRouterState?: boolean;
		keepProvidersState?: boolean;
		keepContentTypeState?: boolean;
	}) {
		// INP case: artificially give the JS thread time to breathe by adding a re-render.
		await new Promise(resolve => setTimeout(resolve, 0));

		const webLocale = languageStore.webLocale;
		const collectionType = payload?.collectionTypeOverride ?? (routingStore.activeCollectionType as CollectionType);

		const filterState: FilterCollection = getDefaultFilterCollection();

		if (payload?.keepContentTypeState) {
			filterState.content_types = currentFilters.value.content_types;
		}

		if (payload?.keepProvidersState) {
			filterState.providers = currentFilters.value.providers;
			filterState.providers_type = currentFilters.value.providers_type;
		}

		setFilters(webLocale, collectionType, filterState);

		TrackingHelper.trackEvent('userinteraction', { action: 'reset' });

		return updateRouterState({ collectionType });
	}

	async function toggle({
		filterName,
		value,
		selectedState,
		trackEvent = true,
		isPremiumFilter = false,
		updateRouteState = true,
		// temporary solution until we refactor this logic
		userSelectedPackages,
	}: {
		filterName: FilterName;
		value: any;
		selectedState?: 1 | 0;
		trackEvent?: boolean;
		isPremiumFilter?: boolean;
		updateRouteState?: boolean;
		userSelectedPackages?: string[];
	}) {
		// INP case: artificially give the JS thread time to breathe by adding a re-render.
		await new Promise(resolve => setTimeout(resolve, 0));

		const userProviders = userSelectedPackages || userStore.settings.providers || [];
		const oldValues = currentFilter(filterName);
		let filterValue = Array.isArray(oldValues) ? oldValues.slice(0) : [];

		// in case 'all' as been toggled, ignore it
		if (value === null && Array.isArray(oldValues) && oldValues.length === 0) {
			return;
		}
		switch (filterName) {
			// only can have 1 value selected at once
			case 'content_types':
			case 'subgenres':
			case 'presentation_types':
			case 'inner_tab':
				const oldValueIsUnset = !oldValues || (Array.isArray(oldValues) && oldValues?.length === 0);
				const oldValueIsDifferent =
					Array.isArray(oldValues) && oldValues?.length === 1 && oldValues[0] !== value;
				const newValueIsValid = value !== null;

				if (oldValueIsUnset || (oldValueIsDifferent && newValueIsValid)) {
					filterValue = [value];
				} else {
					filterValue = [];
				}

				break;
			default:
				if (value === null) {
					filterValue = [];
				} else if (filterValue.includes(value)) {
					// add or remove the value from the arrays
					filterValue.splice(filterValue.indexOf(value), 1);
				} else {
					filterValue.push(value);
				}
				break;
		}

		let newRoute;

		if (filterValue.length === 0 || arrayOfStringsAreEquals(filterValue, userProviders)) {
			newRoute = resetFilter({ filterName, updateRouteState });
		} else {
			newRoute = setFilterValue({ filterName, filterValue, updateRouteState });
		}

		if (trackEvent) {
			const contexts: SnowplowContext[] = [];
			if (isPremiumFilter) {
				const isHideSeenDislikedTitlesEnabled = userStore.isHideSeenDislikedTitlesEnabled ?? false;
				contexts.push(new SnowplowFreemiumContext(isHideSeenDislikedTitlesEnabled));
			}
			TrackingHelper.trackFilterToggleEvent(
				{
					label: filterName,
					property: value ? value : 'all',
					value: selectedState,
				},
				contexts
			);
		}

		return newRoute;
	}

	return {
		state,
		[CollectionType.GLOBAL]: globalGetter,
		[CollectionType.SEENLIST]: seenlistGetter,
		[CollectionType.POPULAR]: popularGetter,
		[CollectionType.SUBGENRE]: subgenreGetter,
		[CollectionType.UPCOMING]: upcomingGetter,
		[CollectionType.NEW]: newGetter,
		[CollectionType.SEARCH]: searchGetter,
		[CollectionType.OFFERS]: offersGetter,
		[CollectionType.TV_SHOW_TRACKING]: tvshowtrackingGetter,
		[CollectionType.MY_LISTS]: mylistsGetter,
		[CollectionType.MY_RENTALS]: myRentalsFilters,
		[CollectionType.PUBLIC_LISTS]: publiclistsGetter,
		[CollectionType.TITLELIST]: titlelistGetter,
		[CollectionType.SPORTS]: sportsGetter,
		[CollectionType.EDITORIAL]: editorialGetter,
		currentFilters,
		filters,
		selectedProvidersType,
		selectedProvidersByType,
		selectedProviders,
		initFiltersPerLocale,
		getWebLocale,
		resetPremiumFilters,
		setFilters,
		publicListsFilters,
		toggle,
		setFilterValue,
		reset,
		resetFilters,
		setSorting,
		setListSorting,
		updateRouterState,
		setRange,
	};
});
